/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { ISelect, ISelectBowtie } from 'plugin-codaf-risk-ext-bowtie/types/engineeringAudit'

import {
    BowtieView,
    EscalationFactorView,
    BarrierView,
    ConsequenceView,
    ThreatView,
    TopEventView
} from '../BowtieManagment/BowtieViews'
import { AbsoluteLoadingContainer } from '../BowtieManagment/styles'
import { useDimensionsEngineering } from '../hooks'
import { getBowtie, listBowties } from '../services'
import { IBowtie, IThreat, IConsequence, ITopEvent, IEscalationFactor, IBarrier } from '../types'
import { Sidebar, View } from './components'
import { ReportsContainer } from './styles'

export interface ITimespan {
    date1: Date
    date2: Date
}

export const EngineeringAudit: React.FC = () => {
    const [preset, setPreset] = useState<ISelect>(null)
    const [bowtieData, setBowtieData] = useState<IBowtie[]>()
    const [inLoading, setInLoading] = useState(true)
    const [bowtieViews, setBowtieViews] = useState<BowtieView[]>([])
    const [totalBowties, setTotalBowties] = useState(0)
    const [currentLoadedBowties, setCurrentLoadedBowties] = useState(0)
    const [startDay, setStartDay] = useState<string | null>()
    const [endDay, setEndDay] = useState<string | null>()
    const [startHour, setStartHour] = useState<string | null>()
    const [endHour, setEndHour] = useState<string | null>()

    const [filterResource, setFilterResource] = useState<ISelect>()
    const [filterBowtie, setFilterBowtie] = useState<ISelectBowtie>()

    const dimensions = useDimensionsEngineering({ height: 75 })

    function getEscalationFactorView(escalationFactor: IEscalationFactor): EscalationFactorView {
        // eslint-disable-next-line  @typescript-eslint/no-use-before-define
        return new EscalationFactorView(escalationFactor, escalationFactor.barriers.map(getBarrierView))
    }
    function getBarrierView(barrier: IBarrier): BarrierView {
        return new BarrierView(barrier, barrier.escalations.map(getEscalationFactorView))
    }

    function getConsequenceView(consequence: IConsequence): ConsequenceView {
        return new ConsequenceView(consequence, consequence.barriers.map(getBarrierView))
    }

    function getThreatView(threat: IThreat): ThreatView {
        return new ThreatView(threat, threat.barriers.map(getBarrierView))
    }

    function getTopEventView(topEvent: ITopEvent): TopEventView {
        return new TopEventView(
            topEvent,
            topEvent.threats.map(getThreatView),
            topEvent.consequences.map(getConsequenceView)
        )
    }

    function getBowtieView(bowtie: IBowtie): BowtieView {
        return new BowtieView(bowtie, getTopEventView(bowtie.topEvent))
    }

    function isValidBowtie(bowtie: IBowtie): boolean {
        const hasConsequenceBarriers = bowtie.topEvent.consequences.every(cons => cons.barriers?.length)
        const hasThreatBarriers = bowtie.topEvent.threats.every(tr => tr.barriers?.length)

        return hasThreatBarriers && hasConsequenceBarriers
    }

    useEffect(() => {
        async function getAllBowties(): Promise<void> {
            try {
                const { data: bowtieList } = await listBowties()
                setTotalBowties(bowtieList.length)
                const bowties: IBowtie[] = []

                await Promise.all(
                    bowtieList.map(async ({ id }) =>
                        getBowtie(id)
                            .then(data => {
                                bowties.push(data)
                                setCurrentLoadedBowties(old => old + 1)
                            })
                            .catch(err =>
                                globalNotification.error(err.message ?? i18n('An error occurred on list bowties.'))
                            )
                    )
                )

                const bowtieWithEvent: BowtieView[] = bowties.filter(isValidBowtie).map(getBowtieView)
                setBowtieViews(bowtieWithEvent)
                setBowtieData(bowties)
                setInLoading(false)
            } catch (err) {
                globalNotification.error(err.message ?? i18n('An error occurred on list bowties.'))
            }
        }
        getAllBowties()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    if (inLoading) {
        return (
            <AbsoluteLoadingContainer>
                <div>
                    <h3>{`${i18n('Loading')} ${
                        totalBowties > 0 ? `${currentLoadedBowties} ${i18n('of')} ${totalBowties}` : '...'
                    }`}</h3>
                </div>
                <div className="progress-wrapper" style={{ position: 'relative', width: 300 }}>
                    <div
                        className="progress-background"
                        style={{
                            width: 300,
                            height: 15,
                            background: 'var(--gray-800)',
                            borderRadius: 3
                        }}
                    >
                        <div
                            className="progress-bar"
                            style={{
                                height: 15,
                                width:
                                    300 * (currentLoadedBowties / totalBowties) > 0
                                        ? 300 * (currentLoadedBowties / totalBowties)
                                        : 0,
                                borderRadius: 3,
                                background: 'var(--blue-500)'
                            }}
                        />
                    </div>
                </div>
            </AbsoluteLoadingContainer>
        )
    }

    return (
        <ReportsContainer style={{ height: dimensions.height }}>
            <Sidebar
                setStartDay={setStartDay}
                setEndDay={setEndDay}
                setStartHour={setStartHour}
                setEndHour={setEndHour}
                startDay={startDay}
                endDay={endDay}
                startHour={startHour}
                endHour={endHour}
                setPreset={setPreset}
                filterResource={filterResource}
                setFilterResource={setFilterResource}
                filterBowtie={filterBowtie}
                setFilterBowtie={setFilterBowtie}
                bowtieData={bowtieData}
            />
            <View
                preset={preset?.value}
                startDay={startDay}
                endDay={endDay}
                startHour={startHour}
                endHour={endHour}
                filterByResource={filterResource?.value}
                filterByBowtie={filterBowtie}
            />
        </ReportsContainer>
    )
}
